import React from 'react';
import { motion } from 'framer-motion';
import { bsmcRules } from '../data/mockData';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

export default function Manifesto() {
  return (
    <div className="min-h-screen pt-28 pb-20 px-6 max-w-5xl mx-auto">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-16 text-center"
      >
        <div className="inline-flex items-center gap-2 text-amber-400 mb-4 px-4 py-1.5 rounded-full bg-amber-500/10 border border-amber-500/30">
          <span className="w-1.5 h-1.5 rounded-full bg-amber-400 pulse-dot" />
          <span className="text-[10px] font-bold tracking-widest uppercase font-mono">BSM-C Living Registry</span>
        </div>
        <h1 className="text-4xl md:text-6xl font-black uppercase tracking-tighter text-zinc-100 mb-4">
          The <span className="text-cyan-400 text-glow-cyan">Hammurabi</span> Blueprint
        </h1>
        <p className="text-zinc-400 max-w-2xl mx-auto text-sm leading-relaxed">
          A sovereign framework for building software that respects the operator. 
          Six strict rules defining the boundary between human sovereignty and machine execution.
        </p>
      </motion.div>

      {/* The 6 Rules of BSM-C */}
      <div className="mb-20">
        <div className="flex items-center gap-3 mb-8 border-b border-zinc-800 pb-4">
          <SafeIcon icon={FiIcons.FiBook} className="text-2xl text-cyan-400" />
          <h2 className="text-2xl font-black uppercase tracking-widest text-zinc-100">
            The 6 Rules of BSM-C
          </h2>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {bsmcRules.map((rule, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.1 }}
              className="group bg-zinc-900/30 border border-zinc-800/50 p-6 rounded-lg hover:border-cyan-500/40 hover:bg-zinc-900/60 transition-all flex flex-col h-full relative overflow-hidden"
            >
              <div className="absolute top-0 right-0 p-4 text-[64px] font-black text-zinc-800/20 leading-none pointer-events-none transition-transform group-hover:scale-110">
                {rule.rule}
              </div>
              
              <div className="flex items-center gap-4 mb-4 relative z-10">
                <div className="w-12 h-12 rounded bg-zinc-950 border border-cyan-500/30 flex items-center justify-center text-cyan-400 group-hover:box-glow-cyan transition-all flex-shrink-0">
                  <SafeIcon icon={rule.icon} className="text-xl" />
                </div>
                <div>
                  <div className="text-[10px] text-cyan-400 font-mono font-bold tracking-widest uppercase mb-1">
                    Rule {String(rule.rule).padStart(2, '0')} // {rule.title}
                  </div>
                  <h3 className="text-lg font-black text-zinc-100 uppercase tracking-wide">
                    {rule.name}
                  </h3>
                </div>
              </div>
              <p className="text-sm text-zinc-400 leading-relaxed relative z-10 mt-auto">
                {rule.desc}
              </p>
            </motion.div>
          ))}
        </div>
      </div>

      {/* The Hammurabi Doctrine */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.6 }}
        className="mb-16"
      >
        <div className="p-8 bg-zinc-900/50 border border-amber-500/20 rounded-lg relative overflow-hidden">
          <div className="absolute top-0 left-0 w-1 h-full bg-amber-500" />
          <h2 className="text-xl font-black uppercase tracking-widest text-zinc-100 mb-4 flex items-center gap-3">
            <SafeIcon icon={FiIcons.FiShield} className="text-amber-400" />
            The Doctrine of Strict Accountability
          </h2>
          <div className="space-y-4 text-sm text-zinc-300 leading-relaxed font-mono">
            <p>
              In antiquity, the Code of Hammurabi established a fundamental principle of engineering accountability: 
              If a builder constructing a house did not make it strong, and it collapsed, the builder was held strictly accountable.
            </p>
            <p>
              The BSM-C architecture brings this consequence to the digital age. Through the <span className="text-fuchsia-400 font-bold">Hammurabi Handoff</span>, 
              we enforce cryptographic accountability. Every state change, every data broadcast, and every semantic evolution is signed, auditable, and immutable.
            </p>
            <p className="text-xl text-amber-400 font-black italic mt-6 border-l-2 border-amber-500/30 pl-4 py-2">
              "An eye for an eye. A state for a state.<br/> 
              Developers are strictly accountable for the logic they commit."
            </p>
          </div>
        </div>
      </motion.div>

      {/* Closing */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.8 }}
        className="mt-16 text-center"
      >
        <div className="text-xs text-amber-400 tracking-widest uppercase mb-3">/// Operator's Oath</div>
        <p className="text-lg text-zinc-200 italic leading-relaxed max-w-2xl mx-auto">
          "I build on bare metal. I sandbox in the Doppelganger.<br />
          I index in the Eduba. I handoff through Hammurabi.<br />
          I leak no telemetry."
        </p>
        <div className="mt-8 text-sm text-cyan-400 text-glow-cyan font-mono font-bold uppercase tracking-[0.3em]">
          End of Line.
        </div>
      </motion.div>
    </div>
  );
}